import { getKiSyokenColumnHalls } from './getKiSyokenColumnHalls';
import { DataKiSyokenResponse } from './types';

type FlatColumnEntry = {
  hallCodeOrAll: string | undefined;
  columnIndex: number;
};

/**
 * 商圏店舗セレクターの絞り込みに応じて、columns / hallsOfMarketArea / rows を整形する。
 *
 * 暫定対応（Issue #6403）:
 *   自店差列はレスポンスに自店セルが含まれないため、自店のみで絞り込むとセルが 0 件になる。
 *   その場合はヘッダー用の column も除去し、テーブルの列ずれを防ぐ。
 *   恒久対応（API 側で自店セルを返す等）が入ったら、この「残セル 0 の商圏列除去」は不要になる可能性がある。
 */
export const dataKiSyokenHiddenByMksHall = (
  dataKiSyoken: DataKiSyokenResponse,
  mksHallsFilter: string[] | undefined
): DataKiSyokenResponse => {
  if (mksHallsFilter == null) {
    return dataKiSyoken;
  }

  const { columns, hallsOfMarketArea, rows } = dataKiSyoken.data;

  // 商圏店舗を考慮したカラムをフラットに展開
  // 商圏店舗に該当する要素には店舗コードを、それ以外はundefinedを格納する
  // 自店差列は自店（先頭ホール）セルがレスポンスに含まれないため、ホール展開から除外する
  const flatColumnEntries = columns.flatMap(
    (column, columnIndex): FlatColumnEntry[] =>
      column.isSyoken
        ? [
            { hallCodeOrAll: 'mksAll', columnIndex },
            ...getKiSyokenColumnHalls(column, hallsOfMarketArea).map(
              (hall) => ({
                hallCodeOrAll: hall.code,
                columnIndex,
              })
            ),
          ]
        : [{ hallCodeOrAll: undefined, columnIndex }]
  );

  const isEmptyHallFilter = mksHallsFilter.length === 0;

  const keptColumnIndexes = new Set(
    columns.flatMap((column, columnIndex) => {
      if (isEmptyHallFilter) {
        return column.isSyoken ? [] : [columnIndex];
      }
      if (column.isSyoken !== true) {
        return [columnIndex];
      }
      // フィルタ後に残る展開セルが 0 の商圏列（自店のみ選択時の自店差など）はヘッダーごと除去する
      const hasRemainingExpandedCell = flatColumnEntries.some(
        (entry) =>
          entry.columnIndex === columnIndex &&
          entry.hallCodeOrAll !== undefined &&
          mksHallsFilter.includes(entry.hallCodeOrAll)
      );
      return hasRemainingExpandedCell ? [columnIndex] : [];
    })
  );

  const filteredColumns = columns.filter((_, columnIndex) =>
    keptColumnIndexes.has(columnIndex)
  );

  const filteredHallsOfMarketArea = hallsOfMarketArea.filter((hall) =>
    mksHallsFilter.includes(hall.code)
  );

  const filteredRows = rows.map((row) => ({
    ...row,
    data: row.data.filter((_, cellIndex) => {
      const entry = flatColumnEntries.at(cellIndex);
      if (entry == null || !keptColumnIndexes.has(entry.columnIndex)) {
        return false;
      }
      if (isEmptyHallFilter) {
        return entry.hallCodeOrAll === undefined;
      }
      return (
        entry.hallCodeOrAll === undefined ||
        mksHallsFilter.includes(entry.hallCodeOrAll)
      );
    }),
  }));

  return {
    ...dataKiSyoken,
    data: {
      ...dataKiSyoken.data,
      columns: filteredColumns,
      hallsOfMarketArea: filteredHallsOfMarketArea,
      rows: filteredRows,
    },
  };
};
